<template>
  <NuxtLink
    v-if="!isAuth"
    :to="'/auth/register'"
    class="pp-comm-unauth"
    data-testid="commission-unauth-cta"
  >
    <div class="icon">
      <svg
        width="22"
        height="22"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        aria-hidden="true"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
        />
      </svg>
    </div>
    <div class="body">
      <p class="lead">{{ t('property.commissionBlock.unauth.lead') }}</p>
      <p class="sub">{{ t('property.commissionBlock.unauth.sub') }}</p>
      <span class="cta"
        >{{ t('property.commissionBlock.unauth.cta') }}
        <span
          class="inline-block rtl:-scale-x-100"
          aria-hidden="true"
          >→</span
        ></span
      >
    </div>
  </NuxtLink>

  <div
    v-else
    class="pp-comm"
    :title="tooltip || undefined"
  >
    <div class="head">
      <span class="lbl">{{ t('property.commissionBlock.label') }}</span>
      <span
        v-if="tierLabel && showTierDetails"
        class="tier-pill"
        >★ {{ tierLabel }}</span
      >
    </div>

    <div
      v-if="isAgent && (tierSplitPercent != null || splitPending)"
      class="split-rows"
    >
      <div class="row">
        <span class="k">{{ t('property.commissionBlock.developerCommission') }}</span>
        <span class="v">{{ poolPercent }}%</span>
      </div>
      <div class="row">
        <span class="k">{{
          t(isUaeShare ? 'property.commissionBlock.yourUaeShare' : 'property.commissionBlock.yourTierSplit')
        }}</span>
        <span class="v">{{ splitPending ? '—' : `${tierSplitPercent}%` }}</span>
      </div>
      <div class="row main">
        <span class="k">{{ t('property.commissionBlock.yourNetCommission') }}</span>
        <span class="v">{{ splitPending ? '—' : `${commissionPercent}%` }}</span>
      </div>
    </div>
    <div
      v-else
      class="split-rows"
    >
      <div class="row main">
        <span class="k">{{ t('property.commissionBlock.projectCommissionOffer') }}</span>
        <span class="v">{{ commissionPercent }}%</span>
      </div>
    </div>

    <div
      v-if="showTierDetails"
      class="est"
    >
      <span class="k">{{ estLabel }}</span>
      <span class="v">
        <span class="cur">{{ currency }}</span>
        {{ formatNumber(estimatedAmount) }}
      </span>
    </div>

    <div
      v-if="showTierDetails"
      class="payout"
    >
      <svg
        viewBox="0 0 24 24"
        width="15"
        height="15"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        aria-hidden="true"
      >
        <circle
          cx="12"
          cy="12"
          r="10"
        />
        <path
          d="M12 6v6l4 2"
          stroke-linecap="round"
        />
      </svg>
      {{ t('property.commissionBlock.paidWithin') }}
    </div>

    <p
      v-if="isAgent && showTierDetails"
      class="locked-note"
    >
      {{ t('property.commissionBlock.lockedNote') }}
    </p>
  </div>
</template>

<script setup lang="ts">
const props = defineProps<{
  isAuth: boolean;
  isAgent: boolean;
  unitTitle?: string | null;
  tierLabel?: string | null;
  poolPercent: number;
  tierSplitPercent: number | null;
  commissionPercent: number;
  estimatedAmount: number;
  currency: string;
  tooltip?: string;
  isUaeShare?: boolean;
  splitPending?: boolean;
}>();

const { t } = useI18n();
const { formatNumber: formatNumberIntl } = useFormat();

// UAE agents see percentages only (same as the app): no tier names, no sample earnings. While the
// split is still loading a UAE agent cannot be told apart, so none of it shows yet.
const showTierDetails = computed(() => !props.isUaeShare && !props.splitPending);

const formatNumber = (value: number) => {
  if (!Number.isFinite(value)) return '0';
  return formatNumberIntl(value, { maximumFractionDigits: 0 });
};

const estLabel = computed(() =>
  props.unitTitle
    ? t('property.commissionBlock.estEarningsOn', { title: props.unitTitle })
    : t('property.commissionBlock.estEarnings'),
);
</script>

<style scoped lang="scss">
.pp-comm-unauth {
  display: flex;
  gap: 14px;
  background: linear-gradient(135deg, #fff8ed, #ffffff);
  border-radius: 16px;
  padding: 18px;
  border: 1px solid rgba(255, 182, 72, 0.4);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  transition: box-shadow 0.15s ease;

  &:hover {
    box-shadow: 0 4px 14px rgba(255, 182, 72, 0.18);
  }

  .icon {
    width: 44px;
    height: 44px;
    border-radius: 999px;
    background: #ffb648;
    color: #0d0f15;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .body {
    flex: 1;
    min-width: 0;

    .lead {
      font-size: 14px;
      font-weight: 700;
      color: #0d0f15;
      margin: 0;
    }

    .sub {
      font-size: 12px;
      color: rgba(13, 15, 21, 0.62);
      margin: 4px 0 8px;
    }

    .cta {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      font-size: 13px;
      font-weight: 700;
      color: #eda436;
    }
  }
}

.pp-comm {
  background: #c6ffc4;
  border-radius: 16px;
  padding: 22px;
  border: 1px solid rgba(34, 197, 94, 0.3);

  .head {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
    gap: 10px;

    .lbl {
      font-size: 13px;
      font-weight: 700;
      color: #0d0f15;
    }

    .tier-pill {
      display: inline-flex;
      align-items: center;
      gap: 5px;
      padding: 3px 8px;
      border-radius: 6px;
      background: white;
      color: #126b2f;
      font-family: 'JetBrains Mono', ui-monospace, monospace;
      font-size: 10px;
      font-weight: 700;
      letter-spacing: 0.08em;
      text-transform: uppercase;
    }
  }

  .split-rows {
    display: flex;
    flex-direction: column;
    gap: 8px;

    .row {
      display: flex;
      justify-content: space-between;
      align-items: baseline;
      font-size: 12.5px;

      .k {
        color: #126b2f;
      }

      .v {
        font-weight: 700;
        color: #0d0f15;
        font-variant-numeric: tabular-nums;
      }

      &.main {
        border-top: 1px solid rgba(18, 107, 47, 0.22);
        padding-top: 10px;
        margin-top: 10px;
        font-size: 14px;

        .k {
          font-weight: 600;
          color: #0d0f15;
        }

        .v {
          font-family: 'Fraunces', 'Times New Roman', serif;
          font-size: 22px;
          font-weight: 600;
          letter-spacing: -0.02em;
        }
      }
    }
  }

  .est {
    margin-top: 14px;
    padding-top: 14px;
    border-top: 1px solid rgba(18, 107, 47, 0.22);
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 12px;

    .k {
      font-size: 12px;
      color: #126b2f;
    }

    .v {
      font-family: 'Fraunces', 'Times New Roman', serif;
      font-weight: 600;
      font-size: 24px;
      letter-spacing: -0.02em;
      color: #0d0f15;

      .cur {
        font-family: 'JetBrains Mono', ui-monospace, monospace;
        font-size: 11px;
        color: #126b2f;
        margin-inline-end: 4px;
        font-weight: 500;
      }
    }
  }

  .payout {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 12.5px;
    color: #126b2f;
    font-weight: 600;
    margin-top: 12px;

    svg {
      width: 15px;
      height: 15px;
    }
  }

  .locked-note {
    margin-top: 6px;
    font-size: 11.5px;
    line-height: 1.45;
    color: rgba(18, 107, 47, 0.78);
    font-style: italic;
  }
}
</style>
